import { useEffect, useState } from 'react'
import { IconCloseButton } from '@/components/Button/IconCloseButton'
import { Caption2, Heading4, Title2 } from '@/components/ui/typography'
import { DESCRIPTION_LIMIT, LAYOUT_NAME_REGEX, TITLE_LIMIT } from '@/routes/Dashboard/types'
import { validateLayout } from '@/routes/Dashboard/utils'
import { Input } from 'components/Input'

interface EditLayoutMetadataModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (name: string, description: string) => void
  currentName: string
  currentDescription: string
}

export const EditLayoutMetadataModal: React.FC<EditLayoutMetadataModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentName,
  currentDescription,
}) => {
  const [layoutName, setLayoutName] = useState(currentName)
  const [layoutDescription, setLayoutDescription] = useState(currentDescription)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setLayoutName(currentName)
      setLayoutDescription(currentDescription)
      setError('')
    }
  }, [isOpen, currentName, currentDescription])

  const handleSave = () => {
    const result = validateLayout({
      name: layoutName,
      description: layoutDescription,
      titleLimit: TITLE_LIMIT,
      descriptionLimit: DESCRIPTION_LIMIT,
      nameRegex: LAYOUT_NAME_REGEX,
    })

    if (!result.isValid) {
      setError(result.error!)
      return
    }

    onSave(result.trimmedName, result.trimmedDescription)

    onClose()
  }

  const handleClose = () => {
    setLayoutName(currentName)
    setLayoutDescription(currentDescription)
    setError('')
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <>
      <div
        className='fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center'
        onClick={handleClose}
      />

      <div
        className='
        fixed left-1/2 top-1/2 z-[1001]
        w-[90%] max-w-[560px] max-h-[85vh]
        -translate-x-1/2 -translate-y-1/2
        flex flex-col
        rounded-xl bg-white shadow-2xl
      '
      >
        <div className='flex items-center justify-between border-b border-black/10 p-6'>
          <Heading4>Edit Layout Settings</Heading4>

          <IconCloseButton onClick={handleClose} />
        </div>

        <div className='flex-1 min-h-0 overflow-y-auto p-6'>
          {/* Layout name */}
          <div className='mb-6'>
            <Title2 className='mb-2 block text-gray-600'>Layout Name *</Title2>

            <Input
              type='text'
              value={layoutName}
              maxLength={TITLE_LIMIT}
              onChange={(e) => {
                setLayoutName(e.target.value.slice(0, TITLE_LIMIT))
                setError('')
              }}
            />

            <Caption2 className='mt-1 text-xs text-gray-400'>
              {layoutName.length}/{TITLE_LIMIT}
            </Caption2>
            {error && <Caption2 className='mt-1 text-sm text-red-500'>{error}</Caption2>}
          </div>
          <div className='mb-1'>
            <Title2 className='mb-2 block text-gray-600'>Description (optional)</Title2>

            <textarea
              value={layoutDescription}
              maxLength={DESCRIPTION_LIMIT}
              onChange={(e) => setLayoutDescription(e.target.value.slice(0, DESCRIPTION_LIMIT))}
              rows={3}
              placeholder='Description of layout...'
              className='
    w-full resize-y rounded-md border border-gray-300
    p-2 text-sm
    focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500
  '
            />
            <Caption2 className='mt-1 mb-4 text-xs text-gray-400'>
              {layoutDescription.length}/{DESCRIPTION_LIMIT}
            </Caption2>
          </div>
        </div>

        <div className='flex justify-end gap-4 border-t border-gray-400 p-6'>
          <button
            onClick={handleClose}
            className='
            rounded-md px-4 py-2
            text-gray-700 hover:bg-gray-100
            transition-colors
          '
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            className='
            rounded-md bg-blue-600 px-4 py-2
            text-white hover:bg-blue-700
            transition-colors
          '
          >
            Save Changes
          </button>
        </div>
      </div>
    </>
  )
}
